import {
  Grid,
  Paper,
  Stack,
  Typography,
  TextField,
  Avatar,
  Box,
  ToggleButtonGroup,
  Chip,
  ToggleButton,
} from "@mui/material";
import sanitizeHtml from "../../../../Utils/sanitizeHtml";
import {
  Bolt,
  Close as CloseIcon,
  Save as SaveIcon,
  DesktopWindows,
  PhoneIphone,
} from "@mui/icons-material";
import SectionHeader from "./SectionHeader";
import { useCallback, useRef } from "react";

const { toast } = require("react-toastify");

const EmailEditor = ({
  form,
  setViewport,
  updateField,
  tags,
  viewport,
  errors,
}) => {
  const emailRef = useRef(null);

  // insert-at-caret helpers
  function insertAtCursor(textareaEl, currentValue, snippet) {
    const start = textareaEl?.selectionStart ?? currentValue.length;
    const end = textareaEl?.selectionEnd ?? currentValue.length;
    const next =
      currentValue.slice(0, start) + snippet + currentValue.slice(end);
    return { next, caret: start + snippet.length };
  }

  const insertEmailVar = useCallback(
    (snippet) => {
      const el = emailRef.current?.querySelector("textarea");
      const value = form.email_html_content || "";
      const { next, caret } = insertAtCursor(el, value, snippet);
      updateField({ target: { name: "email_html_content", value: next } });
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
    },
    [form.email_html_content, updateField],
  );

  const removeTag = useCallback(
    (tagToRemove) => {
      const currentTags = tags.filter((t) => t !== tagToRemove);
      const newRequiredVars = currentTags.join(",");
      updateField({
        target: { name: "required_variables", value: newRequiredVars },
      });
    },
    [tags, updateField],
  );

  return (
    <>
      <Grid container spacing={2}>
        {/* LEFT: dynamic tags / components */}
        <Grid item xs={12} md={3}>
          <Paper
            variant="outlined"
            sx={{ p: 2, position: { md: "sticky" }, top: { md: 8 } }}
          >
            <Typography variant="overline" color="text.secondary">
              COMPONENTS
            </Typography>

            <Box mt={1.5}>
              <Typography variant="subtitle2" gutterBottom>
                Dynamic Tags
              </Typography>
              <Stack
                maxHeight={"auto"}
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                mb={1}
              >
                {tags.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    onClick={() => insertEmailVar(t)}
                    onDelete={() => removeTag(t)}
                  />
                ))}
              </Stack>
              <Stack spacing={2}>
                <TextField
                  label="Template Key"
                  fullWidth
                  autoComplete="off"
                  value={form.template_key}
                  name="template_key"
                  onChange={(e) => {
                    updateField(e);
                  }}
                  error={!!errors.template_key}
                  helperText={errors.template_key}
                  required
                />
                <TextField
                  label="Template Name"
                  fullWidth
                  autoComplete="off"
                  value={form.template_name}
                  onChange={(e) => {
                    updateField(e);
                  }}
                  name="template_name"
                  error={!!errors.template_name}
                  helperText={errors.template_name}
                />
                <TextField
                  label="Tenant ID"
                  fullWidth
                  autoComplete="off"
                  value={form.tenant_id}
                  name="tenant_id"
                  onChange={(e) => {
                    updateField(e);
                  }}
                  error={!!errors.tenant_id}
                  helperText={errors.tenant_id}
                  required
                />
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* MIDDLE: preview */}
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Email Marketing &nbsp;›&nbsp;{" "}
              <strong>{form.template_name || "Template"}</strong>
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewport}
              onChange={(_, v) => v && setViewport(v)}
            >
              <ToggleButton value="desktop">
                <DesktopWindows fontSize="small" sx={{ mr: 0.5 }} />
                Desktop
              </ToggleButton>
              <ToggleButton value="mobile">
                <PhoneIphone fontSize="small" sx={{ mr: 0.5 }} />
                Mobile
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              bgcolor: "#fff",
              ...(viewport === "mobile" ? { width: 420, mx: "auto" } : {}),
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>
                <Bolt fontSize="small" />
              </Avatar>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  FROM:
                </Typography>
                <Typography variant="body2">{form.email_from || ""}</Typography>
              </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              SUBJECT:
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, wordBreak: "break-word" }}>
              {form.email_subject || "Your subject will appear here"}
            </Typography>

            <Paper
              variant="outlined"
              sx={{ p: 3, borderRadius: 2, bgcolor: "#fbfbfd" }}
            >
              <Box
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    form.email_html_content ||
                      "<p>Your email HTML preview will appear here.</p>",
                  ),
                }}
              />
            </Paper>
          </Paper>
        </Grid>

        {/* RIGHT: editor settings */}
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionHeader
              icon={<Bolt fontSize="small" />}
              title="Editor Settings"
            />

            <Stack spacing={2}>
              <TextField
                name="email_from"
                label="Email From"
                fullWidth
                autoComplete="off"
                value={form.email_from}
                onChange={(e) => {
                  updateField(e);
                }}
                placeholder="abc@gmail.com"
                error={!!errors.email_from}
                helperText={errors.email_from}
              />
              <TextField
                label="Email BCC"
                fullWidth
                autoComplete="off"
                value={form.email_bcc}
                name="email_bcc"
                onChange={(e) => {
                  updateField(e);
                }}
                placeholder='abc@gmail.com,xyz@gmail.com'
                error={!!errors.email_bcc}
                helperText='Example: abc@gmail.com,xyz@gmail.com'
              />
              <TextField
                label="Subject Line"
                name="email_subject"
                fullWidth
                autoComplete="off"
                value={form.email_subject}
                onChange={(e) => {
                  updateField(e);
                }}
                error={!!errors.email_subject}
                helperText={errors.email_subject}
              />
              <Typography variant="subtitle2">Email Body (HTML)</Typography>
              <TextField
                label="Email HTML Content"
                name="email_html_content"
                fullWidth
                multiline
                minRows={8}
                autoComplete="off"
                inputRef={emailRef}
                value={form.email_html_content}
                onChange={(e) => {
                  updateField(e);
                }}
                error={!!errors.email_html_content}
                helperText={errors.email_html_content}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 13,
                  },
                }}
              />
              <TextField
                label="Required Variables"
                name="required_variables"
                fullWidth
                autoComplete="off"
                value={form.required_variables}
                onChange={(e) => {
                  updateField(e);
                }}
                helperText='firstname,otp'
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default EmailEditor;
